/*
 * Allowed sources (issue #310) — the client's view of the reader's saved set
 * of source books for one game system.
 *
 * STORAGE IS THE PREFERENCES BAG, NOT A STORE OF ITS OWN. The set lives at
 * `allowedSources[<system-slug>]` inside the one synced preferences row
 * (Tom's standing rule on #291), mirrored locally under the localStorage key
 * `allowedSources` exactly as typography/theme/readerMode mirror theirs —
 * `preferencesSync.collectLocal` picks it up, `applyBag` writes it back, and
 * the SERVER reads the synced row per request to scope lists, search and the
 * generators. This module is only the read/write seam the UI uses.
 *
 * `setAllowedSources` returns `pushNow()`'s promise: the server applies the
 * set from the ROW, not from this browser, so the Account card awaits the
 * push before telling the reader it is saved.
 */

import { cleanAllowedSourcesMap, cleanSourceList, pushNow } from './preferencesSync';

/** Fired on window whenever this browser changes the set, so mounted UI
 *  (the list-page notice) can re-read. Server-sync arrivals fire
 *  PREFERENCES_APPLIED_EVENT instead; listeners watch both. */
export const ALLOWED_SOURCES_CHANGED_EVENT = 'tdt:allowed-sources-changed';

const KEY = 'allowedSources';

function readMap() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    return cleanAllowedSourcesMap(JSON.parse(raw));
  } catch (e) {
    return null;
  }
}

/** The saved set for one system: a non-empty array of rulebook slugs, or null. */
export function getAllowedSources(systemSlug) {
  const map = readMap();
  if (!map || !systemSlug) return null;
  return cleanSourceList(map[systemSlug]);
}

/**
 * Save (or clear, with null/[]) the set for one system, then push the whole
 * preferences snapshot up. Returns pushNow()'s promise (false when the push
 * could not land — the local mirror still holds and the debounced writers
 * will retry on the next preference change).
 */
export function setAllowedSources(systemSlug, slugs) {
  if (!systemSlug) return Promise.resolve(false);
  const map = readMap() || {};
  const clean = cleanSourceList(slugs);
  if (clean) map[systemSlug] = clean;
  else delete map[systemSlug];
  try {
    if (Object.keys(map).length) localStorage.setItem(KEY, JSON.stringify(map));
    else localStorage.removeItem(KEY);
  } catch (e) {
    /* private mode etc. — the push below still carries the intent */
  }
  try {
    window.dispatchEvent(new Event(ALLOWED_SOURCES_CHANGED_EVENT));
  } catch (e) {
    /* non-browser environment */
  }
  // pushNow cancels any pending debounced push and sends the full snapshot;
  // a signed-out save is a deliberate no-op (resolves false).
  return pushNow();
}
